/**
 * Per-symbol chart display settings for symbols with their own settings
 * document (Vest chart feed `*.VEST`, #622): the document's `chartDisplay`
 * section from `GET /api/symbol-settings/{symbol}` (seeded from
 * utt-engine/config/symbol-defaults/<SYMBOL>.json). Overrides the global
 * CME-volume-spike and wick-rejection rule values on that symbol's chart
 * only; CME charts keep the global (localStorage) settings.
 *
 * Only a LOADED answer is cached (a document whose `chartDisplay` is absent
 * caches `null`, a real answer). A failed request, a non-ok response or a
 * document the engine has not loaded yet is never cached: it is retried with
 * backoff (bounded). `refresh` refetches regardless (after a save in the
 * settings panel, and every minute while such a chart is open). Answers are
 * keyed by symbol and a response older than the latest request for that
 * symbol is dropped, so a slow answer for A can never land as B's.
 */
import { create } from 'zustand';
import { getBackendUrl } from '../config/ports';
import { isVestFeedSymbol } from '../utils/vestFeed';
import type { CmeSpikeSettings } from '../utils/cmeVolumeSpikes';

export interface ChartDisplaySettings {
  volumePaneCmeSpike?: Partial<CmeSpikeSettings>;
  wickRejection?: { minWickPct?: number; minWickAtr?: number };
}

interface SymbolChartDisplayState {
  /** Loaded answers only: a symbol missing here is not known yet. */
  bySymbol: Record<string, ChartDisplaySettings | null>;
  /** Fetch once (no-op when known or a request is in flight). */
  ensure: (symbol: string) => Promise<void>;
  /** Fetch now, whatever is cached (settings saved, periodic refresh). */
  refresh: (symbol: string) => Promise<void>;
}

export const CHART_DISPLAY_MAX_RETRIES = 5;
export const CHART_DISPLAY_RETRY_BASE_MS = 1_000;
export const CHART_DISPLAY_REFRESH_MS = 60_000;

/** The `chartDisplay` section of a symbol-settings response, or null. */
export function parseChartDisplay(body: unknown): ChartDisplaySettings | null {
  const s = (body as { settings?: { chartDisplay?: unknown } } | null)?.settings?.chartDisplay;
  if (!s || typeof s !== 'object') return null;
  const o = s as Record<string, unknown>;
  const out: ChartDisplaySettings = {};
  if (o.volumePaneCmeSpike && typeof o.volumePaneCmeSpike === 'object') out.volumePaneCmeSpike = o.volumePaneCmeSpike as Partial<CmeSpikeSettings>;
  if (o.wickRejection && typeof o.wickRejection === 'object') {
    const w = o.wickRejection as Record<string, unknown>;
    out.wickRejection = {
      ...(typeof w.minWickPct === 'number' ? { minWickPct: w.minWickPct } : {}),
      ...(typeof w.minWickAtr === 'number' ? { minWickAtr: w.minWickAtr } : {}),
    };
  }
  return out;
}

// Per-symbol request bookkeeping (module level: not render state).
const latest: Record<string, number> = {};
const inFlight = new Set<string>();
const retries: Record<string, number> = {};
const retryTimers: Record<string, ReturnType<typeof setTimeout>> = {};

/** Test hook: forget every request/retry. */
export function resetChartDisplayRequests(): void {
  for (const k of Object.keys(retryTimers)) clearTimeout(retryTimers[k]);
  for (const o of [latest, retries, retryTimers]) for (const k of Object.keys(o)) delete (o as Record<string, unknown>)[k];
  inFlight.clear();
}

export const useSymbolChartDisplayStore = create<SymbolChartDisplayState>((set, get) => {
  const load = async (key: string): Promise<void> => {
    const my = (latest[key] = (latest[key] ?? 0) + 1);
    inFlight.add(key);
    try {
      const res = await fetch(`${getBackendUrl()}/api/symbol-settings/${encodeURIComponent(key)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { loaded?: boolean };
      if (!body || body.loaded !== true) throw new Error('settings not loaded yet');
      if (latest[key] !== my) return; // a newer request owns the answer
      retries[key] = 0;
      set((s) => ({ bySymbol: { ...s.bySymbol, [key]: parseChartDisplay(body) } }));
    } catch {
      if (latest[key] !== my) return;
      const n = retries[key] ?? 0;
      if (n < CHART_DISPLAY_MAX_RETRIES) {
        retries[key] = n + 1;
        clearTimeout(retryTimers[key]);
        retryTimers[key] = setTimeout(() => {
          delete retryTimers[key];
          void load(key);
        }, CHART_DISPLAY_RETRY_BASE_MS * 2 ** n);
      }
      // otherwise: keep whatever was known (or nothing); the periodic refresh tries again
    } finally {
      if (latest[key] === my) inFlight.delete(key);
    }
  };

  return {
    bySymbol: {},
    ensure: async (symbol: string) => {
      const key = symbol.trim().toUpperCase();
      if (!isVestFeedSymbol(key) || key in get().bySymbol || inFlight.has(key) || key in retryTimers) return;
      await load(key);
    },
    refresh: async (symbol: string) => {
      const key = symbol.trim().toUpperCase();
      if (!isVestFeedSymbol(key)) return;
      clearTimeout(retryTimers[key]);
      delete retryTimers[key];
      retries[key] = 0;
      await load(key);
    },
  };
});
